import { useMutation } from "@apollo/client/react";
import mixpanel from "mixpanel-browser";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";

import EDIT_USER from "../../graphql/mutations/EditUser";
import { alert } from "../common/Alert";
import { DSButton, DSInput } from "../design-system/molecules";

// This code be generalized for other inputs on the settings page when we get there
function AutoEmailInput({
  control,
  setShouldShowAutoExportSave,
  shouldShowAutoExportSave,
  errors,
}) {
  return (
    <div className="flex flex-col items-center">
      <Controller
        control={control}
        render={({ field: { onChange, value } }) => (
          <DSInput
            placeholder="staff@yourclinic.com"
            onChange={(event) => {
              onChange(event.target.value);
              if (!shouldShowAutoExportSave) {
                if (event.target.value.length > 0) {
                  setShouldShowAutoExportSave(true);
                }
              }
            }}
            defaultValue={value}
            type="email"
            dataTestId="auto-email-input"
            error={
              errors.autoEmail
                ? "Please enter a valid email address!"
                : undefined
            }
          />
        )}
        name="autoEmail"
        rules={{
          required: true,
          pattern: {
            value: /\S+@\S+\.\S+/,
            message: "Entered value does not match email format",
          },
        }}
      />
    </div>
  );
}

function SavedAutoEmail({
  data,
  shouldShowAutoExportSave,
  setShouldShowAutoExportSave,
  reset,
  onSubmit,
}) {
  return (
    <div className="flex flex-col md:flex-row md:items-center md:space-x-2 space-y-2 md:space-y-0 mx-2">
      <div className="text-xs md:text-sm text-center font-medium text-primary">
        {data?.user?.autoExportEmail}
      </div>
      {!shouldShowAutoExportSave ? (
        <div className="flex flex-row items-center gap-sm">
          <DSButton
            variant="cancel"
            size="sm"
            onClick={() => {
              reset();
              onSubmit({ autoEmail: "" });
            }}
            dataTestId="auto-email-delete-button"
          >
            Delete
          </DSButton>
          <DSButton
            variant="primary"
            size="sm"
            onClick={() => {
              reset();
              setShouldShowAutoExportSave(true);
            }}
            dataTestId="auto-email-edit-button"
          >
            Edit
          </DSButton>
        </div>
      ) : null}
    </div>
  );
}

export const AutoEmail = ({ data, refetch }) => {
  const [shouldShowAutoExportSave, setShouldShowAutoExportSave] =
    useState(false);

  const [editUser] = useMutation(EDIT_USER);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    // submit auto export mutation
    editUser({ variables: { autoExportEmail: data.autoEmail } })
      .then(() => {
        mixpanel.track("Updated Account Setting", {
          settingName: "Auto Export Email",
          settingKey: "user.autoExportEmail",
          settingValue: data.autoEmail,
        });
        alert("success", "Account updated successfully.");
        setShouldShowAutoExportSave(false);
        refetch();
      })
      .catch((err) => alert("error", err.message));
  };

  return (
    <div
      className={"w-full md:w-auto my-2 flex flex-row items-center"}
    >
      {data?.user?.autoExportEmail && !shouldShowAutoExportSave ? (
        <SavedAutoEmail
          data={data}
          shouldShowAutoExportSave={shouldShowAutoExportSave}
          setShouldShowAutoExportSave={setShouldShowAutoExportSave}
          reset={reset}
          onSubmit={onSubmit}
        />
      ) : (
        <div className="flex flex-col md:flex-row md:items-center md:space-x-2 md:space-y-0 space-y-2">
          <AutoEmailInput
            control={control}
            setShouldShowAutoExportSave={setShouldShowAutoExportSave}
            shouldShowAutoExportSave={shouldShowAutoExportSave}
            errors={errors}
          />
          {shouldShowAutoExportSave ? (
            <div className="flex flex-row items-center gap-sm">
              <DSButton
                variant="cancel"
                size="sm"
                onClick={() => {
                  reset();
                  setShouldShowAutoExportSave(false);
                }}
                dataTestId="auto-email-cancel-button"
              >
                Cancel
              </DSButton>
              <DSButton
                variant="primary"
                size="sm"
                onClick={handleSubmit(onSubmit)}
                dataTestId="auto-email-save-button"
              >
                Save
              </DSButton>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};
